import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Calendar, Users, Loader2, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function BookingModal({
  property,
  propertyId,
  propertyTitle,
  monthlyRent,
  currency,
  open: controlledOpen,
  onClose,
  onBookingSuccess,
}) {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? controlledOpen : internalOpen;
  const resolvedPropertyId = propertyId || property?.property_id || property?.id || '';
  const resolvedPropertyTitle = propertyTitle || property?.title || 'this property';
  const resolvedCurrency = currency || property?.currency || '$';
  const resolvedMonthlyRent = Number(
    monthlyRent ?? property?.price ?? property?.monthly_rent ?? property?.monthlyRent ?? 0
  );

  const [formData, setFormData] = useState({
    move_in_date: '',
    duration: 1,
    message: '',
  });

  const setOpen = (nextOpen) => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    if (!nextOpen && onClose) {
      onClose();
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const calculateTotal = () => {
    const duration = Math.max(1, Number(formData.duration) || 1);
    return (resolvedMonthlyRent * duration).toLocaleString(undefined, {
      maximumFractionDigits: 2,
    });
  };

  const formatPrice = (value) => {
    if (!Number.isFinite(value) || value <= 0) return 'Price on request';
    return `${resolvedCurrency} ${value.toLocaleString()}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.error('Please log in to request a booking');
      navigate('/login', { state: { from: `/property/${resolvedPropertyId}` } });
      return;
    }

    if (!resolvedPropertyId) {
      toast.error('Property details are still loading. Please try again.');
      return;
    }

    if (!formData.move_in_date) {
      toast.error('Please select a move-in date');
      return;
    }

    if (formData.duration < 1) {
      toast.error('Duration must be at least 1 month');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          property_id: resolvedPropertyId,
          move_in_date: formData.move_in_date,
          duration: parseInt(formData.duration),
          message: formData.message,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create booking request');
      }

      toast.success('Booking request sent successfully!');
      setOpen(false);
      setFormData({ move_in_date: '', duration: 1, message: '' });

      if (onBookingSuccess) {
        onBookingSuccess();
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button size="lg" className="w-full bg-accent hover:bg-accent/90">
            <Calendar className="mr-2 h-5 w-5" />
            Request Booking
          </Button>
        </DialogTrigger>
      )}

      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request Booking</DialogTitle>
          <DialogDescription>
            Send a booking request for {resolvedPropertyTitle}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Move-in Date */}
          <div className="space-y-2">
            <Label htmlFor="move-in-date">Move-in Date *</Label>
            <Input
              id="move-in-date"
              type="date"
              value={formData.move_in_date}
              onChange={(e) => handleInputChange('move_in_date', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="duration">Lease Duration (Months) *</Label>
              <span className="text-sm font-medium text-slate-600">{formData.duration} month(s)</span>
            </div>
            <div className="flex gap-2">
              <Input
                id="duration"
                type="number"
                min="1"
                max="24"
                value={formData.duration}
                onChange={(e) => handleInputChange('duration', e.target.value)}
                className="flex-1"
              />
            </div>
            <p className="text-xs text-slate-500">Select between 1 and 24 months</p>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">Message to Owner (Optional)</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder="Tell the owner about yourself, your profession, or why you're interested in this property..."
              rows={3}
            />
          </div>

          {/* Price Summary */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-50 rounded-lg p-4 space-y-2"
          >
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Monthly Rent</span>
              <span className="font-medium text-slate-900">
                {formatPrice(resolvedMonthlyRent)}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Duration</span>
              <span className="font-medium text-slate-900">{formData.duration} month(s)</span>
            </div>
            <div className="border-t border-slate-200 pt-2 flex items-center justify-between">
              <span className="font-semibold text-slate-900">Total Price</span>
              <span className="text-lg font-bold text-accent">
                {Number.isFinite(resolvedMonthlyRent) && resolvedMonthlyRent > 0
                  ? `${resolvedCurrency} ${calculateTotal()}`
                  : 'Price on request'}
              </span>
            </div>
          </motion.div>

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
            The property owner will review your request and confirm or decline the booking.
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-accent hover:bg-accent/90"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending Request...
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                Send Booking Request
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
